import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { ApiResponse, PaginatedResponse } from '../axios';
import { ENDPOINTS } from '../endpoints';
import {
  RepairTicket,
  TicketSearchParams,
  CreateTicketRequest,
  DiagnoseTicketRequest,
  ApproveTicketRequest,
  CompleteTicketRequest,
  DeliverTicketRequest,
  AddTicketNoteRequest,
  EditTicketNoteRequest,
  AddTicketDocumentationPhotosRequest,
  RemoveTicketDocumentationPhotoRequest,
  SetTicketInvoiceLinkRequest,
  SetTicketPaymentLinkRequest,
  SetTicketReceiptLinkRequest,
  ReceiptPreview,
  SubmitDamageReportRequest,
  AddTicketPartRequest,
  RecordTicketPaymentRequest,

  SetTicketLaborCostRequest,
  OverrideBillingPriceRequest,
  AddPriceOffsetRequest,
  RequestPriceRevisionRequest,
  RequestRepairAdditionRequest,
  EditRepairAdditionRequest,
  EditTicketPartRequest,
  DecideRepeatIssueRequest,
  TicketStatusCounts,
} from '../types/ticket.types';
import type { PaymentMethod } from '../types/common.types';
import { UrgencyLevel, RepairPriority } from '../types/common.types';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../errors';
import { notificationKeys } from './useNotifications';

// The intake form collects urgency as UrgencyLevel (numeric, request-only), but the backend's
// RepairPriority is a string enum (JsonStringEnumConverter) - without this mapping, a raw number
// was sent for `priority` and silently ignored by model binding, so intake always fell back to
// the DTO default (Normal) no matter what the user picked.
// Exported so CustomerIntakePage (self-intake) can classify urgency identically to this form
// instead of growing its own copy that could drift out of sync.
export const URGENCY_TO_PRIORITY: Record<UrgencyLevel, RepairPriority> = {
  [UrgencyLevel.Low]: RepairPriority.Low,
  [UrgencyLevel.Normal]: RepairPriority.Normal,
  [UrgencyLevel.High]: RepairPriority.High,
  [UrgencyLevel.Urgent]: RepairPriority.Urgent,
};

// Query keys
export const ticketKeys = {
  all: ['tickets'] as const,
  lists: () => [...ticketKeys.all, 'list'] as const,
  list: (params: TicketSearchParams) => [...ticketKeys.lists(), params] as const,
  details: () => [...ticketKeys.all, 'detail'] as const,
  detail: (id: string) => [...ticketKeys.details(), id] as const,
  statusCounts: () => [...ticketKeys.all, 'status-counts'] as const,
};

// Get tickets list
export function useTickets(params: TicketSearchParams = {}) {
  return useQuery({
    queryKey: ticketKeys.list(params),
    queryFn: async () => {
      const response = await api.get<ApiResponse<PaginatedResponse<RepairTicket>>>(
        ENDPOINTS.TICKETS.BASE,
        { params }
      );
      return response.data.data!;
    },
    // Overrides the app-wide 5-minute default (main.tsx). This is the screen a technician has open
    // waiting for new customer-submitted tickets to show up - with the 5-minute default, revisiting
    // or remounting the page within that window served the OLD cached snapshot with no network
    // request at all, so a ticket created a minute ago simply wasn't there yet regardless of how
    // many times the page was reloaded. The live SignalR push (useTicketListUpdates) is supposed to
    // call invalidateQueries the moment a ticket is created, which bypasses staleTime entirely - but
    // that still leaves every *other* path to this screen (a fresh page load, a dropped connection,
    // a background tab) waiting out the same 5 minutes. Zero means every mount/focus refetches.
    staleTime: 0,
  });
}

// Get single ticket
export function useTicket(id: string) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<any>>(
        ENDPOINTS.TICKETS.BY_ID(id)
      );
      const t = response.data.data!;
      // The API returns flat fields (customerName/deviceName/technicianName…).
      // Normalize them into the nested shape the detail page reads.
      return {
        ...t,
        customer: t.customer ?? {
          name: t.customerName,
          phone: t.customerPhone,
          email: t.customerEmail,
        },
        device: t.device ?? {
          manufacturer: t.deviceName,
          model: '',
          type: t.deviceType,
          imei: t.deviceImei,
        },
        technician:
          t.technician ??
          (t.technicianName ? { name: t.technicianName, phone: t.technicianPhone } : undefined),
      } as RepairTicket;
    },
    enabled: !!id,
  });
}

// Get status counts
export function useTicketStatusCounts() {
  return useQuery({
    queryKey: ticketKeys.statusCounts(),
    queryFn: async () => {
      const response = await api.get<ApiResponse<TicketStatusCounts>>(
        ENDPOINTS.TICKETS.STATUS_COUNTS
      );
      return response.data.data!;
    },
    // See the comment on useTickets - same 5-minute default, same "new ticket" screen.
    staleTime: 0,
  });
}

// Create ticket
export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTicketRequest) => {
      // The intake form's request shape differs from the backend CreateTicketRequest DTO.
      // Map field names so the ticket is actually created:
      // customerId→existingCustomerId, device→newDevice{brand}, urgency→priority,
      // notes→customerNotes, customerSignature→signatureData.
      const payload = {
        existingCustomerId: data.customerId,
        existingDeviceId: data.deviceId,
        newDevice: data.device
          ? {
              type: data.device.type,
              brand: data.device.manufacturer,
              model: data.device.model,
              imei: data.device.imei,
              serialNumber: data.device.serialNumber,
              color: data.device.color,
              condition: data.device.screenCondition,
              bodyCondition: data.device.bodyCondition,
              hasWaterDamage: data.device.hasWaterDamage,
              conditionNotes: data.device.conditionNotes,
              photoUrls: data.device.photoUrls,
              // Device only has one passcode slot - password takes precedence, PIN is the fallback.
              password: data.credentials?.password || data.credentials?.pin,
              patternLock: data.credentials?.patternLock,
              credentialsNote: data.credentials?.note,
            }
          : undefined,
        problemDescription: data.problemDescription,
        problemCategories: data.problemCategories,
        customerNotes: data.notes,
        priority: data.urgency != null ? URGENCY_TO_PRIORITY[data.urgency] : undefined,
        accessories: data.accessories,
        signatureData: data.customerSignature,
        termsAccepted: true,
        overrideOverdueDebtBlock: data.overrideOverdueDebtBlock,
        overrideOverdueDebtReason: data.overrideOverdueDebtReason,
        // Must be mapped explicitly like everything else here - this object is the whole payload,
        // so a field missing from it is dropped silently (found live 2026-10-04: a clerk's ₪50 was
        // ignored and the ₪100 tenant default used instead). undefined and 0 are NOT the same:
        // undefined means "not specified, use the default", 0 means "staff decided not to charge".
        diagnosisFee: data.diagnosisFee,
      };
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.BASE,
        payload
      );
      return response.data.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      toast.success('הקריאה נוצרה בהצלחה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה ביצירת הקריאה'));
    },
  });
}

// Start diagnosis (assign technician, Received -> InDiagnosis)
export function useStartDiagnosis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, technicianId }: { id: string; technicianId: string }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.START_DIAGNOSIS(id),
        { technicianId }
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(id) });
      toast.success('האבחון החל — הכרטיס שויך לטכנאי');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהתחלת האבחון'));
    },
  });
}

// Diagnose ticket
export function useDiagnoseTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DiagnoseTicketRequest }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.DIAGNOSE(id),
        data
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(id) });
      toast.success('האבחון נשמר בהצלחה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשמירת האבחון'));
    },
  });
}

// Approve ticket
export function useApproveTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    // Required, not optional - an omitted body 400s with ASP.NET's generic "The request field is
    // required" before the backend's own validation ever runs (found 2026-08-19: QueuePage's
    // drag-to-approve was still calling this without data after TicketDetailPage's button was
    // already fixed for the same bug).
    mutationFn: async ({ id, data }: { id: string; data: ApproveTicketRequest }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.APPROVE(id),
        data
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(id) });
      toast.success('הקריאה אושרה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה באישור הקריאה'));
    },
  });
}

// Reject ticket
export function useRejectTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.REJECT(id),
        { reason }
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: ['loanerDevices'] });
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(id) });
      toast.success('הקריאה נדחתה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בדחיית הקריאה'));
    },
  });
}

// Cancel ticket - summary and reason both required (boss's ask, 2026-09-09)
export function useCancelTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, summary, reason }: { id: string; summary: string; reason: string }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.CANCEL(id),
        { summary, reason }
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: ['loanerDevices'] });
      toast.success('הקריאה בוטלה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בביטול הקריאה'));
    },
  });
}

// Start repair (transition to InProgress)
export function useStartRepair() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.START(id)
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(id) });
      toast.success('התחלת עבודה על התיקון');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהתחלת התיקון'));
    },
  });
}

// Complete ticket
export function useCompleteTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CompleteTicketRequest }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.COMPLETE(id),
        data
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(id) });
      toast.success('התיקון הושלם');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בסגירת הקריאה'));
    },
  });
}

// Deliver ticket
export function useDeliverTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DeliverTicketRequest }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.DELIVER(id),
        data
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: ['loanerDevices'] });
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(id) });
      toast.success('המכשיר נמסר ללקוח');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה במסירת המכשיר'));
    },
  });
}

// Assign a loaner device to a ticket - the boss's request, 2026-09-16.
export function useAssignLoanerDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, loanerDeviceId }: { id: string; loanerDeviceId: string }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.LOANER_DEVICE(id),
        { loanerDeviceId }
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['loanerDevices'] });
      toast.success('המכשיר החלופי הוצמד לקריאה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהצמדת מכשיר חלופי'));
    },
  });
}

export function useMarkLoanerDeviceReturned() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.LOANER_DEVICE_RETURN(id)
      );
      return response.data.data!;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['loanerDevices'] });
      toast.success('המכשיר החלופי סומן כמוחזר');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בסימון החזרת המכשיר החלופי'));
    },
  });
}

export function useRemoveLoanerDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.LOANER_DEVICE(id)
      );
      return response.data.data!;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['loanerDevices'] });
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהסרת מכשיר חלופי'));
    },
  });
}

// Mark ticket as waiting for a part to arrive
export function useSetWaitingForParts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, partsDescription }: { id: string; partsDescription: string }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.WAITING_FOR_PARTS(id),
        { partsDescription }
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(id) });
      toast.success('הקריאה סומנה כממתינה לחלקים');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בסימון הקריאה כממתינה לחלקים'));
    },
  });
}

// Resume repair once the awaited part(s) arrived
export function usePartsArrived() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.PARTS_ARRIVED(id)
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.ticket(id) });
      toast.success('הקריאה חזרה לתיקון');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון הקריאה'));
    },
  });
}

// Add note to ticket
export function useAddTicketNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AddTicketNoteRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.NOTES(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('ההערה נוספה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהוספת ההערה'));
    },
  });
}

// Only the original author may edit their own note - server enforces this too (EDIT_NOTE_FAILED),
// this hook doesn't add its own gate beyond what the UI already checks before showing the button.
export function useEditTicketNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, noteId, data }: { id: string; noteId: string; data: EditTicketNoteRequest }) => {
      const response = await api.put(ENDPOINTS.TICKETS.EDIT_NOTE(id, noteId), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('ההערה עודכנה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון ההערה'));
    },
  });
}

// Add documentation photos (already-uploaded URLs) to a ticket
export function useAddTicketDocumentationPhotos() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AddTicketDocumentationPhotosRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.DOCUMENTATION_PHOTOS(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('התמונות נוספו');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהעלאת התמונות'));
    },
  });
}

// Remove a single documentation photo (technician-added, not the customer's intake photos)
export function useRemoveTicketDocumentationPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: RemoveTicketDocumentationPhotoRequest }) => {
      const response = await api.delete(ENDPOINTS.TICKETS.DOCUMENTATION_PHOTOS(id), { data });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('התמונה נמחקה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה במחיקת התמונה'));
    },
  });
}

// Set (or clear) the ticket's externally-issued invoice link
export function useSetTicketInvoiceLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SetTicketInvoiceLinkRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.INVOICE_LINK(id), data);
      return response.data;
    },
    onSuccess: (_, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      // Saving (not clearing) a link also fires an automatic SMS/WhatsApp to the customer -
      // the boss's explicit ask - so staff should know that happened, not just that it saved.
      toast.success(data.invoiceLinkUrl ? 'הקישור נשמר ונשלחה הודעה ללקוח' : 'הקישור לחשבונית הוסר');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשמירת הקישור'));
    },
  });
}

// Creates a real invoice via YesInvoice and fills the ticket's invoice link automatically -
// alternative to useSetTicketInvoiceLink's manual paste, not a replacement for it (2026-08-16).
export function useCreateYeshInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.TICKETS.CREATE_YESHINVOICE(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('החשבונית נוצרה ונשלחה הודעה ללקוח');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה ביצירת החשבונית'));
    },
  });
}

// Creates a חשבונית מס (DocumentType 8, tax invoice only) via YesInvoice - the new document flow's
// step before Complete()/payment (the boss's final call, 2026-09-02). Unlike useCreateYeshInvoice
// (DocumentType 9, combined invoice+receipt), this works before the ticket is paid or completed.
export function useCreateYeshInvoiceTaxInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.TICKETS.CREATE_YESHINVOICE_TAX_INVOICE(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('החשבונית נוצרה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה ביצירת החשבונית'));
    },
  });
}

export function useCancelTaxInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.TICKETS.CANCEL_TAX_INVOICE(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('החשבונית בוטלה ביש-חשבונית');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בביטול החשבונית'));
    },
  });
}

export function useCreateYeshInvoiceReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.TICKETS.CREATE_YESHINVOICE_RECEIPT(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('הקבלה נוצרה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה ביצירת הקבלה'));
    },
  });
}

// Takes the diagnosis fee at the counter and issues its own חשבונית מס קבלה in one action.
export function useCollectDiagnosisFee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, method }: { id: string; method: PaymentMethod }) => {
      const response = await api.post(ENDPOINTS.TICKETS.COLLECT_DIAGNOSIS_FEE(id), { method });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      // A payment can clear debt shown on the customer page, the debts tab and the delivery warning.
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('עלות האבחון נגבתה וחשבונית מס קבלה הופקה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בגביית עלות האבחון'));
    },
  });
}

// Fee collected in YesInvoice itself (terminal or cash) - records it with the document issued there.
export function useRecordDiagnosisFeeDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { method: PaymentMethod; documentUrl: string; documentNumber?: number } }) => {
      const response = await api.post(ENDPOINTS.TICKETS.DIAGNOSIS_FEE_DOCUMENT(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('עלות האבחון נרשמה כשולמה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה ברישום עלות האבחון'));
    },
  });
}

// Sends the customer a payment link for the diagnosis fee, or the existing one again.
export function useSendDiagnosisFeePaymentLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.TICKETS.DIAGNOSIS_FEE_PAYMENT_LINK(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('קישור לתשלום נשלח ללקוח');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשליחת קישור לתשלום'));
    },
  });
}

// Manual retry for a fee paid through a link whose document failed to issue automatically.
export function useIssueDiagnosisFeeDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.TICKETS.ISSUE_DIAGNOSIS_FEE_DOCUMENT(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('חשבונית מס קבלה לעלות האבחון הופקה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהפקת החשבונית'));
    },
  });
}

// Creates a hosted YesInvoice payment link for the ticket's current balance due and sends it to
// the customer - PHASE 1 only, creates the link + a Pending Payment but doesn't yet verify
// completion (2026-08-20).
export function useCreateYeshInvoicePaymentLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.TICKETS.CREATE_YESHINVOICE_PAYMENT_LINK(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('קישור התשלום נוצר ונשלחה הודעה ללקוח');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה ביצירת קישור התשלום'));
    },
  });
}

// Asks YesInvoice whether the tax invoice was paid through its own link; quiet = the automatic check on open.
export function useSyncInvoicePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string; quiet?: boolean }) => {
      const response = await api.post<ApiResponse<boolean>>(ENDPOINTS.TICKETS.SYNC_INVOICE_PAYMENT(id));
      return response.data.data;
    },
    onSuccess: (paid, { id, quiet }) => {
      if (paid) {
        queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
        toast.success('התשלום בקישור של החשבונית התקבל ונרשם בקריאה');
      } else if (!quiet) {
        toast('עדיין לא התקבל תשלום בקישור של החשבונית');
      }
    },
    onError: (error, { quiet }) => {
      if (!quiet) toast.error(extractErrorMessage(error, 'שגיאה בבדיקת התשלום ביש-חשבונית'));
    },
  });
}

// Set (or clear) the ticket's independent online payment link
export function useSetTicketPaymentLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SetTicketPaymentLinkRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.PAYMENT_LINK(id), data);
      return response.data;
    },
    onSuccess: (_, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success(data.paymentLinkUrl ? 'הקישור נשמר ונשלחה הודעה ללקוח' : 'קישור התשלום הוסר');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשמירת הקישור'));
    },
  });
}

// Set (or clear) the ticket's receipt link - required before delivery. No customer notification
// on save (unlike invoice/payment links above) - this documents money already collected, not a
// new link the customer needs to act on.
export function useSetTicketReceiptLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SetTicketReceiptLinkRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.RECEIPT_LINK(id), data);
      return response.data;
    },
    onSuccess: (_, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success(data.receiptLinkUrl ? 'הקישור לקבלה נשמר' : 'הקישור לקבלה הוסר');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשמירת הקישור'));
    },
  });
}

// Reads a pasted yeshbe.co receipt and shows what recording it will do - errors are shown inline by the caller.
export function usePreviewPastedReceipt() {
  return useMutation({
    mutationFn: async ({ id, url }: { id: string; url: string }) => {
      const response = await api.post(ENDPOINTS.TICKETS.RECEIPTS_PREVIEW(id), { url });
      return response.data.data as ReceiptPreview;
    },
  });
}

// A catalog product the customer bought at the counter with the repair - billed on top, no price approval.
export function useAddTicketAccessory() {
  const queryClient = useQueryClient();

  return useMutation({
    // No partId = a one-off product not in the catalog (name, price and cost required).
    mutationFn: async ({ id, ...body }: { id: string; quantity: number; partId?: string; name?: string; unitPrice?: number; costPrice?: number }) => {
      const response = await api.post(ENDPOINTS.TICKETS.ACCESSORIES(id), body);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['parts'] });
      toast.success('המוצר נוסף לקריאה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהוספת המוצר'));
    },
  });
}

// Records the payment the pasted receipt documents (or attaches it to one recorded by hand).
export function useRecordPastedReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, url, includesStorePurchase = false }: { id: string; url: string; includesStorePurchase?: boolean }) => {
      const response = await api.post(ENDPOINTS.TICKETS.RECEIPTS(id), { url, includesStorePurchase });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('התשלום נרשם לפי הקבלה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה ברישום הקבלה'));
    },
  });
}

// File a "דיווח על נזק" (damage report) on a ticket - persists it and emails the tenant's
// configured damage-report address (if one is set in Settings).
export function useSubmitDamageReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SubmitDamageReportRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.DAMAGE_REPORTS(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('הדיווח נשמר ונשלח');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשליחת הדיווח'));
    },
  });
}

// Add part to ticket
export function useAddTicketPart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AddTicketPartRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.PARTS(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('החלק נוסף');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהוספת החלק'));
    },
  });
}

// Record a payment (typically a deposit) against an open ticket
export function useRecordTicketPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: RecordTicketPaymentRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.PAYMENTS(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      // A payment can clear debt shown on the customer page, the debts tab and the delivery warning.
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('התשלום נקלט');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בקליטת התשלום'));
    },
  });
}

// Set labor cost mid-repair (independent of completing the ticket)
export function useSetTicketLaborCost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SetTicketLaborCostRequest }) => {
      const response = await api.patch(ENDPOINTS.TICKETS.LABOR_COST(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('עלות העבודה עודכנה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון עלות העבודה'));
    },
  });
}

// Goodwill offset the customer SEES on the invoice - not the same as useOverrideBillingPrice
// below, which changes the price with nothing shown to the customer. Accumulates per call.
export function useAddPriceOffset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AddPriceOffsetRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.PRICE_OFFSET(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('הקיזוז נוסף');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהוספת הקיזוז'));
    },
  });
}

// Admin/manager: change a completed repair's warranty (days 0 = none), with a reason.
export function useSetWarranty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, days, reason }: { id: string; days: number; reason: string }) => {
      const response = await api.put(ENDPOINTS.TICKETS.WARRANTY(id), { days, reason });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('האחריות עודכנה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון האחריות'));
    },
  });
}

export function useClearPriceOffset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(ENDPOINTS.TICKETS.PRICE_OFFSET(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('הקיזוז בוטל');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בביטול הקיזוז'));
    },
  });
}

// Explicitly change what a ticket bills, overriding the customer-approved price
export function useOverrideBillingPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: OverrideBillingPriceRequest }) => {
      const response = await api.patch(ENDPOINTS.TICKETS.BILLING_OVERRIDE(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('מחיר החיוב עודכן');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון מחיר החיוב'));
    },
  });
}

// Send a revised quote back to the customer for approval
export function useRequestPriceRevision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: RequestPriceRevisionRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.PRICE_REVISION(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      toast.success('הצעת המחיר המעודכנת נשלחה ללקוח');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשליחת הצעת מחיר מעודכנת'));
    },
  });
}

// Technician's decision on a ticket flagged as a suspected repeat issue - decides whether it
// bills as warranty work or as a new, chargeable issue (see backend RepairTicket.DecideRepeatIssue).
export function useDecideRepeatIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DecideRepeatIssueRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.REPEAT_DECISION(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('ההחלטה נשמרה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשמירת ההחלטה'));
    },
  });
}

// Assign technician to ticket
export function useAssignTechnician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, technicianId }: { id: string; technicianId: string }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.ASSIGN(id),
        { technicianId }
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      toast.success('הטכנאי שויך לקריאה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשיוך הטכנאי'));
    },
  });
}

// Unassign technician from ticket
export function useUnassignTechnician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const response = await api.post<ApiResponse<RepairTicket>>(
        ENDPOINTS.TICKETS.UNASSIGN(id)
      );
      return response.data.data!;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      toast.success('הטכנאי הוסר מהקריאה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהסרת הטכנאי'));
    },
  });
}

// Sends the customer an addition to the approved price for approval (backend RepairTicket.RequestRepairAddition).
export function useRequestRepairAddition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: RequestRepairAdditionRequest }) => {
      const response = await api.post(ENDPOINTS.TICKETS.REPAIR_ADDITION(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      toast.success('התוספת נשלחה ללקוח לאישור');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשליחת התוספת ללקוח'));
    },
  });
}

// Parts came out above the approved price - the customer approves the real total on the tracking link.
export function useRequestPriceIncrease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await api.post(ENDPOINTS.TICKETS.PRICE_INCREASE(id), { reason });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      toast.success('נשלח ללקוח לאישור המחיר');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשליחה ללקוח'));
    },
  });
}

// Bill the approved price anyway - the rest shows as a discount on the invoice.
export function useAcceptPriceOverrun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.TICKETS.ACCEPT_PRICE_OVERRUN(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      toast.success('ימשיכו במחיר שאושר');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון'));
    },
  });
}

export function useEditRepairAddition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: EditRepairAdditionRequest }) => {
      const response = await api.put(ENDPOINTS.TICKETS.REPAIR_ADDITION(id), data);
      return response.data;
    },
    onSuccess: (_, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      toast.success(data.amount === 0 ? 'התוספת לתיקון הוסרה' : 'התוספת לתיקון עודכנה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון התוספת'));
    },
  });
}

export function useEditTicketPart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, lineId, data }: { id: string; lineId: string; data: EditTicketPartRequest }) => {
      const response = await api.put(ENDPOINTS.TICKETS.PART_LINE(id, lineId), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['parts'] });
      toast.success('החלק עודכן');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון החלק'));
    },
  });
}

export function useRemoveTicketPart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, lineId }: { id: string; lineId: string }) => {
      const response = await api.delete(ENDPOINTS.TICKETS.PART_LINE(id, lineId));
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['parts'] });
      toast.success('החלק הוסר מהקריאה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בהסרת החלק'));
    },
  });
}

// Intake: is the selected device back within its last repair's warranty (then no diagnosis fee)?
export function useDeviceWarrantyCheck(customerId: string | null, deviceId: string | null) {
  return useQuery({
    queryKey: ['tickets', 'warranty-check', customerId, deviceId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<{ withinWarranty: boolean; previousTicketNumber?: string | null; warrantyExpiresAt?: string | null }>>(
        ENDPOINTS.TICKETS.WARRANTY_CHECK,
        { params: { customerId, deviceId } }
      );
      return response.data.data!;
    },
    enabled: !!customerId && !!deviceId,
    staleTime: 0,
  });
}

// Diagnosis found the device can't be repaired - the customer is told and asked to confirm.
export function useReportUnrepairable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await api.post(ENDPOINTS.TICKETS.UNREPAIRABLE(id), { reason });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      toast.success('ההודעה נשלחה ללקוח');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשליחת ההודעה ללקוח'));
    },
  });
}

// Marks an open ticket premium - its unpaid diagnosis fee (and any pending fee link) is dropped.
export function useMarkPremium() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(ENDPOINTS.TICKETS.PREMIUM(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      toast.success('הקריאה סומנה כקריאת פרימיום');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בסימון קריאת פרימיום'));
    },
  });
}
